import { Component, Input, OnInit, inject } from "@angular/core";
import { MatDialog, MatDialogConfig } from "@angular/material/dialog";
import { MatCell, MatCellDef, MatColumnDef, MatHeaderCell, MatHeaderCellDef, MatHeaderRow, MatHeaderRowDef, MatRow, MatRowDef, MatTable, MatTableDataSource } from "@angular/material/table";
import { FlexDirective, FlexFillDirective, LayoutAlignDirective, LayoutDirective } from "@ngbracket/ngx-layout/flex";
import { ForgeCardModule, ForgeDividerModule, ForgeIconButtonModule, ForgeIconModule, ForgeLinearProgressModule, ForgeTooltipModule } from "@tylertech/forge-angular";
import { IShellService } from "@tylertech/incode-module";
import { IChartCriteria } from "@tylertech/tyler-api-types";
import { NewPacketTemplateDTO } from "../../interfaces";
import { UBDashboardService } from "../../services";
import { NewPacketTemplateDialogComponent, NewPacketTemplateDialogData } from "../dialogs";

type PacketColumn = "module" | "process" | "description" | "launch" | "edit" | "delete";

// TODO - this ain't no chart....
@Component({
    selector: "utilities-new-packet-template-card",
    templateUrl: "./utilitiesNewPacketTemplateCard.tmpl.html",
    styleUrls: [
        "./utilitiesNewPacketTemplateCard.component.scss"
    ],
    imports: [
        FlexDirective,
        LayoutAlignDirective,
        LayoutDirective,
        FlexFillDirective,
        ForgeCardModule,
        ForgeDividerModule,
        ForgeLinearProgressModule,
        ForgeIconButtonModule,
        ForgeIconModule,
        ForgeTooltipModule,
        MatCell,
        MatCellDef,
        MatColumnDef,
        MatHeaderCell,
        MatHeaderCellDef,
        MatHeaderRow,
        MatHeaderRowDef,
        MatRow,
        MatRowDef,
        MatTable,
    ]
})
export class UtilitiesNewPacketTemplateCardComponent implements OnInit
{
    private readonly $shellService = inject(IShellService);
    private readonly $ubDashboardService = inject(UBDashboardService);
    private readonly matDialog = inject(MatDialog);

    // Unused - the card loads its own templates; accepted so it can be bound like the other dashboard charts.
    @Input() chartCriteria: IChartCriteria;

    readonly tableData: MatTableDataSource<NewPacketTemplateDTO>;
    readonly columnNames: ReadonlyArray<PacketColumn> = ["module", "process", "description", "launch", "edit", "delete"];
    private readonly initialCount: number = 5;

    private _items: Array<NewPacketTemplateDTO>;
    private showCount: number;
    isSaving: boolean;

    constructor()
    {
        this.tableData = new MatTableDataSource<NewPacketTemplateDTO>();
    }

    private get items(): Array<NewPacketTemplateDTO> { return this._items; }
    private set items(value: Array<NewPacketTemplateDTO>)
    {
        this._items = value;
        this.updateTableData();
    }

    get canShowMore(): boolean { return this.items?.length > this.showCount; }
    get hasItems(): boolean { return this.items?.length !== 0; }

    ngOnInit(): void
    {
        this.loadItems();
    }

    private loadItems(): void
    {
        this.$ubDashboardService.getNewPacketTemplates()
            .then(result =>
            {
                this.setShowCount(this.initialCount);

                this.items = result;
            });
    }

    private setShowCount(count: number): void
    {
        this.showCount = Math.min(count, this.items?.length ?? this.initialCount);
    }

    getLaunchAriaLabel(item: NewPacketTemplateDTO): string
    {
        return `Launch-${item.Process}-${item.Module}-UtilityBilling`;
    }

    getEditAriaLabel(item: NewPacketTemplateDTO): string
    {
        return `Edit-${item.Process}-${item.Module}-UtilityBilling`;
    }

    getDeleteAriaLabel(item: NewPacketTemplateDTO): string
    {
        return `Delete-${item.Process}-${item.Module}-UtilityBilling`;
    }

    delete(item: NewPacketTemplateDTO): void
    {
        this.isSaving = true;
        this.$ubDashboardService.deleteNewPacketTemplate(item)
            .then((_success: boolean) =>
            {
                this.items = this.items.filter(x => x !== item);
            })
            .finally(() =>
            {
                this.isSaving = false;
            });
    }

    launch(item: NewPacketTemplateDTO): void
    {
        if (this.isSaving)
        {
            return;
        }

        this.isSaving = true;
        this.$shellService.openShellFunction("CreateProcessPacket", item.Id, false, "Failed to create packet ")
            .finally(() =>
            {
                this.isSaving = false;
            });
    }

    newPacketTemplate(item?: NewPacketTemplateDTO): void
    {
        const editing: boolean = item !== null && item !== undefined;
        const dialogConfig: MatDialogConfig<NewPacketTemplateDialogData> = { data: { Item: item } };
        const dialogRef = this.matDialog.open(NewPacketTemplateDialogComponent, dialogConfig);

        dialogRef.afterClosed().toPromise()
            .then((result: NewPacketTemplateDTO) =>
            {
                if (result)
                {
                    if (editing)
                    {
                        this.items.splice(this.items.findIndex(i => i.Id === item.Id), 1, result);
                    }
                    else
                    {
                        this.items.push(result);
                    }

                    this.showCount = this.items.length;
                    this.updateTableData();
                }
            });
    }

    showMore($event?: MouseEvent): void
    {
        $event?.stopPropagation();

        this.setShowCount(this.showCount + this.initialCount);
        this.updateTableData();
    }

    private updateTableData(): void
    {
        this.tableData.data = this._items.slice(0, this.showCount);
    }
}
